import { showWorkflowStateToast } from "@/lib/richToasts";
import type { ToastMetaItem } from "@/components/toasts/RichToastContent";

interface WorkflowLifecycleToastOptions {
  workflowId?: string | null;
  workflowName?: string | null;
}

interface WorkflowPublishedToastOptions extends WorkflowLifecycleToastOptions {
  version?: number | null;
  description?: string;
}

interface WorkflowRollbackToastOptions extends WorkflowLifecycleToastOptions {
  version: number;
  description?: string;
}

interface WorkflowSharingUpdatedToastOptions extends WorkflowLifecycleToastOptions {
  visibility: string;
}

interface WorkflowWidgetEnabledToastOptions extends WorkflowLifecycleToastOptions {
  widgetTitle: string;
  widgetUrl: string;
}

export function showWorkflowPublishedToast({
  workflowId,
  workflowName,
  version,
  description = "Latest edits are now live.",
}: WorkflowPublishedToastOptions) {
  const meta: ToastMetaItem[] = [];

  if (version != null) {
    meta.push({ label: `v${version}` });
  }

  return showWorkflowStateToast({
    type: "success",
    title: version != null ? `Published v${version}` : "Workflow published",
    workflowId,
    workflowName,
    description,
    meta,
  });
}

// Back-compat alias — publish-is-the-only-activation in the new model.
export function showWorkflowActivatedToast(options: WorkflowLifecycleToastOptions) {
  return showWorkflowResumedToast(options);
}

export function showWorkflowResumedToast({ workflowId, workflowName }: WorkflowLifecycleToastOptions) {
  return showWorkflowStateToast({
    type: "success",
    title: "Resumed",
    workflowId,
    workflowName,
    description: "Triggers are firing again.",
  });
}

export function showWorkflowPausedToast({ workflowId, workflowName }: WorkflowLifecycleToastOptions) {
  return showWorkflowStateToast({
    type: "info",
    title: "Paused",
    workflowId,
    workflowName,
    description: "Triggers stopped. Resume to start firing again.",
  });
}

export function showWorkflowArchivedToast({ workflowId, workflowName }: WorkflowLifecycleToastOptions) {
  return showWorkflowStateToast({
    type: "info",
    title: "Archived",
    workflowId,
    workflowName,
    description: "Moved to the Archived tab.",
    omitOpenWorkflowLink: true,
  });
}

export function showWorkflowUnarchivedToast({ workflowId, workflowName }: WorkflowLifecycleToastOptions) {
  return showWorkflowStateToast({
    type: "info",
    title: "Restored",
    workflowId,
    workflowName,
    description: "Back in the active workflow list.",
  });
}

export function showWorkflowRollbackToast({
  workflowId,
  workflowName,
  version,
  description,
}: WorkflowRollbackToastOptions) {
  return showWorkflowStateToast({
    type: "info",
    title: `Revert loaded · v${version}`,
    workflowId,
    workflowName,
    description: description ?? `v${version} is loaded as edits. Publish to make it live.`,
    meta: [{ label: `v${version}` }],
  });
}

export function showWorkflowRevertLoadedToast({ workflowId, workflowName, version }: WorkflowRollbackToastOptions) {
  return showWorkflowRollbackToast({ workflowId, workflowName, version });
}

export function showWorkflowDeletedToast({ workflowId, workflowName }: WorkflowLifecycleToastOptions) {
  return showWorkflowStateToast({
    type: "success",
    title: "Permanently deleted",
    workflowId,
    workflowName,
    description: "The workflow and its history were removed.",
  });
}

export function showWorkflowSharingUpdatedToast({
  workflowId,
  workflowName,
  visibility,
}: WorkflowSharingUpdatedToastOptions) {
  return showWorkflowStateToast({
    type: "success",
    title: "Sharing updated",
    workflowId,
    workflowName,
    description: "Visibility and ownership changes are now live.",
    meta: [{ label: visibility.replaceAll("_", " ") }],
  });
}

export function showWorkflowWidgetEnabledToast({
  workflowId,
  workflowName,
  widgetTitle,
  widgetUrl,
}: WorkflowWidgetEnabledToastOptions) {
  return showWorkflowStateToast({
    type: "success",
    title: "Widget enabled",
    workflowId,
    workflowName,
    description: widgetTitle,
    meta: [{ label: "public widget" }],
    button: { title: "Open widget", href: widgetUrl },
  });
}

export function showWorkflowWidgetDisabledToast({ workflowId, workflowName }: WorkflowLifecycleToastOptions) {
  return showWorkflowStateToast({
    type: "info",
    title: "Widget disabled",
    workflowId,
    workflowName,
    description: "The public entry point has been taken offline.",
    meta: [{ label: "public widget" }],
  });
}

export function showWorkflowDraftDiscardedToast(options: WorkflowLifecycleToastOptions) {
  return showWorkflowEditsDiscardedToast(options);
}

export function showWorkflowEditsDiscardedToast({ workflowId, workflowName }: WorkflowLifecycleToastOptions) {
  return showWorkflowStateToast({
    type: "success",
    title: "Edits discarded",
    workflowId,
    workflowName,
    description: "Back on the published version.",
  });
}
